import { addLayer, createImageLayer, pageOf, setImageFill } from '../../features/ArtEditor/headless';
import { placeImage } from '../../data/artImages';

// The run's open image question (contract 2.9.0), or null.
export const openImageQuestion = run => (run?.status === 'waiting_user'
  ? run.pending_actions?.find(action => action.status === 'open' && action.kind === 'images') || null : null);

// Generation is offered for a place when the account has it and the place has a ceiling.
export const generationOffered = (features, place) => !!features?.art_image_generation && place.generation_credits != null;

// A suggested image (`StudioImageSuggestion`) in the shape the image grids take.
export const suggestionImage = item => ({ id: item.asset_id, url: item.url, width: item.width, height: item.height, description: item.description || null, origin: 'upload' });

// A place that already shows an image: not empty (2.10.0's `placeholder`), so keeping it is an answer.
export const hasImage = place => !!place?.current && place.current.kind !== 'placeholder';

// Whether a place is answered (2.12.0: every place needs one before the dialog answers): an
// image, a generation, or keeping the image it already shows.
export const isChosen = (place, choice) => !!choice && (choice.source !== 'keep' || hasImage(place));

// The next place still to answer after `fromId`, going round; null when no other is left.
export function nextPending(places, choices, fromId) {
  const at = places.findIndex(place => place.slot_id === fromId);
  const order = [...places.slice(at + 1), ...places.slice(0, Math.max(0, at))];
  return order.find(place => !isChosen(place, choices[place.slot_id]))?.slot_id || null;
}

// What a place starts with: the AI's own pick (the creator's image it reused), else nothing,
// for the creator to choose (2.12.0; keeping is never assumed, not even the AI's `keep`). A
// `default` never starts a generation the account does not offer.
export function initialChoice(place, features) {
  const pick = place.default;
  if (pick?.source === 'asset' && pick.asset_id) {
    const found = (place.suggested || []).find(item => item.asset_id === pick.asset_id);
    return { source: 'asset', asset_id: pick.asset_id, image: found?.url ? suggestionImage(found) : null };
  }
  if (pick?.source === 'community' && pick.community_image_id) return { source: 'community', community_image_id: pick.community_image_id, image: null };
  if (pick?.source === 'generate' && generationOffered(features, place)) return { source: 'generate' };
  return null;
}

// How the creator's own photo for a place should look: the AI's tip (2.12.0 `photo_tip`), else
// a generic one by mode (a person or detail, or anything else).
export const photoTip = (t, place) => place.photo_tip?.trim() || t(`imagesTipGeneric_${place.mode === 'sunburst' ? 'sunburst' : 'flare'}`);

// The shape the place asks the photo to have.
export function orientationOf(box) {
  const ratio = box?.width ? box.height / box.width : 1;
  return ratio > 1.1 ? 'vertical' : ratio < 0.9 ? 'horizontal' : 'square';
}

// A gallery or community image picked for a place.
export const imageChoice = image => (image.kind === 'library'
  ? { source: 'community', community_image_id: image.id, image }
  : { source: 'asset', asset_id: image.id, image });

// The id the grids ring for a choice (asset and community ids never collide: `sas-` / `sci-`).
export const choiceImageId = choice => (choice?.source === 'asset' ? choice.asset_id : choice?.source === 'community' ? choice.community_image_id : null);

// The image a choice shows. Signed asset URLs expire after 15 minutes while a question can
// wait for hours: a suggested image is read again from the place (re-signed whenever the run
// is read), and the choice's own copy is only the fallback.
function imageOf(place, choice) {
  if (choice?.source !== 'asset') return choice?.image || null;
  const found = (place?.suggested || []).find(item => item.asset_id === choice.asset_id && item.url);
  return found ? suggestionImage(found) : choice.image || null;
}

// The thumbnail a place shows: the picked image, else the one it keeps (a generated one has
// none until it exists, a place still to answer none).
export function choiceThumb(place, choice) {
  if (choice?.source === 'asset' || choice?.source === 'community') {
    const image = imageOf(place, choice);
    return image?.thumbnail || image?.url || null;
  }
  return choice?.source === 'keep' ? place.current?.url || null : null;
}

// The line under a place: what will happen to it, or that it is still to choose.
export function choiceLabel(t, place, choice) {
  if (!isChosen(place, choice)) return t('imagesPending');
  if (choice.source === 'generate') return t('imagesChoice_generate');
  if (choice.source === 'asset') return t('imagesChoice_asset');
  if (choice.source === 'community') return t('imagesChoice_community');
  return t('imagesKeep');
}

// The source an image choice puts in the art (the full `url`, never the thumbnail).
function sourceOf(place, choice) {
  const image = imageOf(place, choice);
  if (!image?.url) return null;
  if (choice.source === 'asset') return { kind: 'asset', asset_id: choice.asset_id, url: image.url, width: image.width, height: image.height };
  if (choice.source === 'community') return { kind: 'library', asset_id: null, url: image.url, width: image.width, height: image.height };
  return null;
}

// The draft with a place's choice in it, placed the way the run will place it (`placeImage`,
// core's ArtImagePlacement), so the creator judges the result: a slot's photo; a frame's
// fill, with its shade, the text on it turned white and its icons hidden; a page background
// with its shade. Where that placement cannot run, the bare photo. A generation (or a keep)
// shows the draft as it is.
export function withChoice(doc, place, choice) {
  const source = sourceOf(place, choice);
  if (!doc || !source) return doc;
  const placed = placeImage(doc, place, source);
  if (placed?.document) return placed.document;
  if (place.layer_id) return doc.pages.some(page => page.layers.some(layer => layer.id === place.layer_id)) ? setImageFill(doc, place.layer_id, source) : doc;
  const page = pageOf(doc, place.page_id);
  const layer = { ...createImageLayer(doc, source, { binding: 'photo' }), x: 0, y: 0, width: doc.width, height: doc.height, locked: true };
  return addLayer(doc, page.id, layer, 0);
}

// The answer (`StudioImageChoice[]`): one per place, only the fields its source takes. A
// subject the creator left as the AI wrote it goes as null. The dialog sends it only once
// every place is chosen; a place without a choice would keep what it has.
export function answerOf(places, choices, subjects) {
  return places.map(place => {
    const choice = choices[place.slot_id] || { source: 'keep' };
    const item = { slot_id: place.slot_id, source: choice.source };
    if (choice.source === 'asset') item.asset_id = choice.asset_id;
    if (choice.source === 'community') item.community_image_id = choice.community_image_id;
    if (choice.source === 'generate') {
      const edited = (subjects[place.slot_id] ?? '').trim().slice(0, 300);
      item.subject = edited && edited !== place.subject ? edited : null;
    }
    return item;
  });
}
